import React, { useId } from "react";
import IconContainer from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";

const HEXAGON_PATH =
  "M20.7846 3.0718C23.2598 1.64273 26.3094 1.64273 28.7846 3.0718L41.5692 10.453C44.0444 11.8821 45.5692 14.5231 45.5692 17.3812V32.1436C45.5692 35.0017 44.0444 37.6427 41.5692 39.0718L28.7846 46.453C26.3094 47.8821 23.2598 47.8821 20.7846 46.453L8 39.0718C5.52479 37.6427 4 35.0017 4 32.1436V17.3812C4 14.5231 5.52479 11.8821 8 10.453L20.7846 3.0718Z";

const BADGE_PATH =
  "M43.7846 27.9171C45.0222 27.2026 46.547 27.2026 47.7846 27.9171L53.3109 31.1077C54.5485 31.8222 55.3109 33.1427 55.3109 34.5718V40.953C55.3109 42.3821 54.5485 43.7026 53.3109 44.4171L47.7846 47.6077C46.547 48.3222 45.0222 48.3222 43.7846 47.6077L38.2583 44.4171C37.0207 43.7026 36.2583 42.3821 36.2583 40.953V34.5718C36.2583 33.1427 37.0207 31.8222 38.2583 31.1077L43.7846 27.9171Z";

const CHECK_PATH = "M49.7846 34.7624L44.2846 40.2624L41.7846 37.7624";

type LogoSize = "md" | "lg";

const sizeClasses: Record<LogoSize, string> = {
  md: "size-12",
  lg: "size-16"
};

interface IntegrationLogoProps {
  icon: React.ReactNode;
  size?: LogoSize;
  showConnectedBadge?: boolean;
  className?: string;
}

const IntegrationLogo: React.FC<IntegrationLogoProps> = ({
  icon,
  size = "md",
  showConnectedBadge = false,
  className
}) => {
  const id = useId().replace(/:/g, "");
  const overlayId = `integration-logo-overlay-${id}`;
  const strokeId = `integration-logo-stroke-${id}`;
  const badgeStrokeId = `integration-logo-badge-stroke-${id}`;

  return (
    <div className={cn("relative shrink-0", sizeClasses[size], className)}>
      <svg
        className="absolute inset-0 size-full"
        viewBox="0.78 0.76 48 48"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={overlayId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#FAFCFD" stopOpacity="0.24" />
            <stop offset="1" stopColor="#0C111D" stopOpacity="0.16" />
          </linearGradient>
          <linearGradient id={strokeId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#475467" stopOpacity="0.31" />
            <stop offset="1" stopColor="#475467" />
          </linearGradient>
        </defs>
        <path d={HEXAGON_PATH} fill="#98A2B3" fillOpacity="0.18" />
        <path d={HEXAGON_PATH} fill={`url(#${overlayId})`} opacity="0.1" />
        <path d={HEXAGON_PATH} stroke={`url(#${strokeId})`} strokeWidth="0.5" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <IconContainer size={size} className="[&>svg]:size-full">
          {icon}
        </IconContainer>
      </div>
      {showConnectedBadge && (
        <svg
          className="absolute left-[73%] top-[55%] h-[46%] w-[42%]"
          viewBox="35.5 26.8 20.5 22"
          fill="none"
          data-test="integration-connected-badge"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={badgeStrokeId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#2CE588" />
              <stop offset="1" stopColor="#2CE588" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={BADGE_PATH} fill="#161E2C" />
          <path d={BADGE_PATH} fill="#0CC675" fillOpacity="0.2" />
          <path
            d={BADGE_PATH}
            stroke={`url(#${badgeStrokeId})`}
            strokeWidth="0.8"
          />
          <path
            d={CHECK_PATH}
            stroke="#2CE588"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

export default IntegrationLogo;
